/**
 * This class is concerned with validating routes and products (menu options). It also performs top level handling of
 * route changes (like changing the selected organization when an organization id is included in the route).
 */

import {_} from 'lodash';
import * as ko from "knockout";
import Router from "../Router";
import {Login} from "./Login";
import {PermissionValidator} from "./PermissionValidator";
import {IKnockoutComponent, IMessageData, IRouteDefinitionParams, ISnackbarData, TRouteErrorReason} from "../index";
import {getEadmRole} from "../util/misc";
import {Permission} from "../constants";
import PromiseRequest from "../req/PromiseRequest";
import {config, global} from "../appContext";
import {getRouteDefinition} from "../constants/RouteDefinitions";
import {hasher} from 'hasher';
import {nsAmplify} from "../util/nsAmplify";
import {nsRoute} from "../util/route";
import {getPageShellNavigationKey, shouldMountPageShell} from "../util/pageShellRoute";
import {captureIntendedHash} from "./navigateAfterLogin";
import {TabManager} from "../component/tab-list/TabManager";

export class RouteValidator {

    // Depending on lack of permissions, these messages may be communicated to the user.
    private static MESSAGES: { [key in TRouteErrorReason]?: IMessageData } = {
        'not_logged_in': {
            message: `you need to be logged in to access this information`,
            type: "info"
        },
        'cookies_not_supported': {
            message: `cookies must be supported to log in`,
            type: 'danger',
        }
    };

    // Views can make use of this message data to communicate what's going on to the user.
    messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);

    /** Keep one PageShell component instance across soft tab navigations (details + content shells). */
    private pageShellComponentCache: {
        key: string;
        component: IKnockoutComponent;
        tabManager?: TabManager;
    } | null = null;

    constructor(private router: Router,
                private login: Login,
                private permissionValidator: PermissionValidator,
                private snackbarData: KnockoutObservable<ISnackbarData | null>) {
        this.router.currentRoute.subscribe(this.onRouteChange.bind(this));
        this.login.selectedOrganization.subscribe(this.updateMessage.bind(this));
        this.onRouteChange();
    }

    public getRouteComponent(): IKnockoutComponent | null {
        // encapsulate implementation in a pure computed to take advantage of Knockout's caching of results
        return this.routeComponent();
    }

    /** Live TabManager for the cached soft-nav PageShell (Router soft path fallback). */
    public getCachedPageShellTabManager(): TabManager | null {
        return this.pageShellComponentCache?.tabManager
            ?? this.pageShellComponentCache?.component?.params?.__tabManager
            ?? null;
    }

    /** PageShell calls this after creating TabManager so soft-nav can find it without currentRoute attach races. */
    public notePageShellTabManager(tabManager: TabManager): void {
        const cache = this.pageShellComponentCache;
        if (!cache) {
            return;
        }
        cache.tabManager = tabManager;
        if (cache.component?.params) {
            cache.component.params.__tabManager = tabManager;
        }
    }

    private routeComponent = ko.pureComputed((): IKnockoutComponent | null => {
        const errorReason = this.getErrorReason();
        if (errorReason) {
            this.pageShellComponentCache = null;
            const ret = this.getErrorComponent(errorReason);
            if (!ret) {
                switch (errorReason) {
                    case "missing_license": {
                        const httpDef = getRouteDefinition('http');
                        if (!httpDef?.component) {
                            return null;
                        }
                        const ret = httpDef.component as IKnockoutComponent;
                        ret.params.id = 403;
                        return ret;
                    }
                    case "insufficient_permissions": {
                        const httpDef = getRouteDefinition('http');
                        if (!httpDef?.component) {
                            return null;
                        }
                        const ret = httpDef.component as IKnockoutComponent;
                        ret.params.id = 401;
                        return ret;
                    }
                    case "not_logged_in": {
                        const formLoginDef = getRouteDefinition('formlogin');
                        return formLoginDef?.component as IKnockoutComponent ?? null;
                    }
                }
            }
            return ret;
        }

        const cr = this.router.currentRoute();
        if (!cr?.__routeDef) {
            this.pageShellComponentCache = null;
            return null;
        }
        const softKey = getPageShellNavigationKey(cr);
        if (softKey && this.pageShellComponentCache?.key === softKey) {
            return this.pageShellComponentCache.component;
        }

        // Omit live managers before cloneDeep — they are circular / non-cloneable and were
        // attached to currentRoute after first PageShell paint (createcustomer soft-nav path).
        const routeDef = cr.__routeDef;
        const crClone = _.cloneDeep(_.omit(cr, ['__tabManager', '__requestRouteData'])) as IRouteDefinitionParams;
        let comp = _.cloneDeep(routeDef.component);
        const shellDefForMount = crClone.__shellRouteDef ?? (
            shouldMountPageShell(crClone) ? routeDef : null
        );
        if (shouldMountPageShell(crClone)) {
            comp = {name: 'component/page-shell/details', params: {}};
        }
        delete crClone.__routeDef;
        if (comp) {
            _.extend(comp.params, crClone);
            // PageShell resolves content/tabs from __routeDef; keep the shell def after the delete above.
            if (shellDefForMount && shouldMountPageShell(cr)) {
                comp.params.__routeDef = shellDefForMount;
                if (!comp.params.__shellRouteDef) {
                    comp.params.__shellRouteDef = shellDefForMount;
                }
            }
        }
        const component = comp as IKnockoutComponent;
        this.pageShellComponentCache = softKey && component
            ? {key: softKey, component}
            : null;
        return component;
    })

    private getErrorComponent(errorReason: TRouteErrorReason): IKnockoutComponent | null {
        const cr = this.router.currentRoute();
        const ec = cr?.__routeDef?.errorComponents?.[errorReason];
        if (!ec) {
            return null;
        }
        switch (typeof ec) {
            case 'string':
                return {name: ec};
            case 'object':
                return ec;
            default:
                return null;
        }
    }

    onRouteChange() {
        if (config.isNewVersion) {
            window.requestAnimationFrame(() => window.location.reload())
            return
        }
        this.syncCachedPageShellTabFromRoute();
        this.updateOrganization();
        this.updateMessage();
        this.updateLastHash();
    }

    /**
     * When currentRoute notifies for the same soft-nav shell (should be rare after Router soft path),
     * drive TabManager from the new tab id without relying on a remount.
     */
    private syncCachedPageShellTabFromRoute(): void {
        const cr = this.router.currentRoute();
        if (!cr) {
            return;
        }
        const softKey = getPageShellNavigationKey(cr);
        const cache = this.pageShellComponentCache;
        if (!softKey || !cache || cache.key !== softKey) {
            return;
        }
        this.refreshCachedPageShellParams(cr);
        const tabId = cr.__tabRouteDef?.id ?? cr.__routeId;
        const tabManager = cr.__tabManager ?? cache.tabManager ?? cache.component?.params?.__tabManager;
        if (tabManager) {
            cache.tabManager = tabManager;
            if (cache.component?.params) {
                cache.component.params.__tabManager = tabManager;
            }
            if (!cr.__tabManager) {
                cr.__tabManager = tabManager;
            }
            if (tabId) {
                tabManager.setTabId(tabId);
            }
        }
    }

    /** Keep soft-nav cache params aligned with the live route tab fields. */
    private refreshCachedPageShellParams(cr: IRouteDefinitionParams): void {
        const cache = this.pageShellComponentCache;
        if (!cache?.component?.params) {
            return;
        }
        const params = cache.component.params;
        if (cr.__routeId != null) {
            params.__routeId = cr.__routeId;
        }
        if (cr.__tabRouteDef != null) {
            params.__tabRouteDef = cr.__tabRouteDef;
        }
        if (cr.__routeParams != null) {
            params.__routeParams = cr.__routeParams;
        }
        if (cr.__id != null) {
            params.__id = cr.__id;
        }
    }

    private routeRequest!: PromiseRequest;

    /**
     * Matched route `{orgnr}` owns the working org (covers non-`o/` hashes like formview/{orgnr}/…).
     * Skip formlogin so pre-auth company memory stays separate from the header picker.
     */
    private updateOrganization() {
        const cr = this.router.currentRoute();
        if (!cr?.__routeDef || cr.__routeDef.id === 'formlogin') {
            return;
        }
        this.login.syncSelectedOrganizationFromRoute(cr.__routeParams?.['orgnr']);
    }

    private updateMessage() {
        const errorReason = this.getErrorReason()
        if (!errorReason) {
            this.messageData(null);
        } else {
            this.messageData(RouteValidator.MESSAGES[errorReason] ?? null);
        }
    }

    // Updates the local storage with (a valid) route.
    private updateLastHash() {
        const cr = this.router.currentRoute();
        if (this.login.isLoggedIn() && !this.getErrorReason() && cr?.__routeDef?.storeHash !== false && cr?.__routeParams?.request_) {
            nsAmplify.store("lastHash", cr.__routeParams.request_);
        }
    }

    /**
     * Checks if the current route is valid for the user. If not it returns an error. Otherwise null is returned.
     * @returns {string|null}
     */
    private getErrorReason(): TRouteErrorReason | null {
        const cr = this.router.currentRoute();
        if (!cr?.__routeDef) {
            return null;
        }
        const routeDef = cr.__routeDef;
        const pl = routeDef.permissionLevel;
        if (pl === Permission.None) {
            return null;
        }

        const org = this.login.selectedOrganization();

        if (org && org.readonlyeadm) {
            const hash = hasher.getHash();
            if (hash.indexOf("create") >= 0 || hash.indexOf("edit") >= 0) {
                return "insufficient_permissions";
            }
        }

        if (org && !nsRoute.isRouteLicenseOkay(routeDef, org)) {
            return "missing_license";
        }

        // does page require a permission level?
        if (pl) {

            // user is not logged in?
            if (!this.login.isLoggedIn()) {
                const isCookieSupported = navigator.cookieEnabled ?? true;

                if (!isCookieSupported) {
                    return 'cookies_not_supported';
                }

                // Keep the first captured deep link; a protected click while logged out can set
                // it, but auth-chrome hashes must not overwrite an existing destination.
                captureIntendedHash(document.location.hash)
                return "not_logged_in";
            }

            if (pl == Permission.LoggedIn) {
                return null;
            }

            if (routeDef.canSeeOwnPage && global.isLoggedInUser()) {
                return null;
            }

            // user does not have a sufficient permission level?
            const sufficientPermission = getEadmRole(org ?? undefined) >= pl;
            if (!sufficientPermission) {
                return "insufficient_permissions";
            }
        }

        return null;
    }

    // A helper function to retrieve whether the current route id is the same as the user's id.
    private isUserId(id: string) {
        const user = this.login.user();
        const userId: number | null = user ? user.id : null;
        return userId != null && parseInt(id) === userId;
    }

}
